// Shared render helpers for scheduled task blocks in the time grid.
import { Box } from '@mui/material';
import InlineTaskInput from '@/features/tasks/components/forms/TaskInputInline';
import {
  gridTaskItemSx,
  planDayItemShortFontSize,
  GRID_TASK_DRAG_OPACITY,
  COMPLETED_TASK_OPACITY,
} from '@/components/ui/taskVisualTokens';
import { FIFTEEN_MINUTE_SLOT_HEIGHT } from '@/config/constants';

export const DRAG_OPACITY = GRID_TASK_DRAG_OPACITY;

export const getPrimaryItem = (items) => {
  const found = (items || []).find((p) => p && typeof p === 'string') || null;
  return found ? found.toLowerCase() : null;
};

export const renderNewTaskPlaceholder = (
  style,
  slotHeight,
  onSave,
  onCancel,
  placeholder,
) => (
  <Box
    className="scheduled-task-new"
    data-testid="scheduled-task-new"
    sx={{
      ...gridTaskItemSx,
      position: 'absolute',
      boxSizing: 'border-box',
      zIndex: 5,
      p: '2px 4px',
      display: 'flex',
      alignItems: 'center',
      overflow: 'hidden',
      top: style.top,
      left: style.left,
      width: style.width,
      height: `${slotHeight}px`,
    }}
  >
    <InlineTaskInput
      placeholder={placeholder}
      onSave={onSave}
      onCancel={onCancel}
      closeOnSave
    />
  </Box>
);

export const computeTaskClasses = (
  isCurrent,
  task,
  layoutClass,
  isShort,
  isDragging,
  isOverlay,
) =>
  [
    'scheduled-task',
    isCurrent ? 'current-task' : null,
    task.completed ? 'completed' : null,
    layoutClass,
    isShort ? 'is-short' : null,
    isDragging && !isOverlay ? 'dragging' : null,
    isOverlay ? 'is-overlay' : null,
    task.person ? 'is-delegated' : null,
  ]
    .filter(Boolean)
    .join(' ');

export const computeBoxSx = (
  isCurrent,
  isDragging,
  isOverlay,
  task,
  isShort,
) => ({
  ...gridTaskItemSx,
  mb: 0,
  ...(isShort
    ? {
        p: '0 4px',
        fontSize: planDayItemShortFontSize,
        justifyContent: 'center',
      }
    : { p: '2px 4px', justifyContent: 'flex-start' }),
  position: 'relative',
  zIndex: isCurrent ? 3 : 1,
  touchAction: 'none',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  overflow: 'hidden',
  bgcolor: task.completed ? 'neutral.doneBg' : 'grey.200',
  opacity:
    (isDragging && !isOverlay ? DRAG_OPACITY : 1) *
    (task.completed ? COMPLETED_TASK_OPACITY : 1),
  borderRadius: '3px',
  boxShadow: 'none',
  border: 1,
  borderColor: isCurrent ? 'primary.main' : 'neutral.border',
  color: task.completed ? 'text.subtle' : 'text.primary',
  transition: 'none',
  boxSizing: 'border-box',
  '& .task-resize-handle': { opacity: 0.45 },
  '&:hover': !isOverlay
    ? {
        transform: 'none',
        boxShadow: 'none',
        zIndex: 10,
        '& .task-resize-handle': { opacity: 1 },
        '& .task-resize-handle::after': { bgcolor: 'grey.600' },
      }
    : {},
  backgroundImage: task.person
    ? 'repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(0, 0, 0, 0.06) 4px, rgba(0, 0, 0, 0.06) 8px)'
    : 'none',
  cursor: isOverlay ? 'grabbing' : 'grab',
});

export const mergePositionStyles = (currentHeight, style) => ({
  ...(typeof currentHeight === 'number' && !Number.isNaN(currentHeight)
    ? { height: `${currentHeight}px` }
    : style.height
      ? { height: style.height }
      : {}),
  ...(style.top
    ? { top: style.top, position: style.position || 'absolute' }
    : {}),
  ...(style.left ? { left: style.left } : {}),
  ...(style.right ? { right: style.right } : {}),
  ...(style.width ? { width: style.width } : {}),
});

export const DEFAULT_SCHEDULED_TASK_PROPS = {
  style: {},
  fifteenMinuteSlotHeight: FIFTEEN_MINUTE_SLOT_HEIGHT,
  navigate: () => {},
  currentTaskId: null,
  onSave: () => {},
  onCancel: () => {},
  isOverlay: false,
};
